// React & core
import { useEffect, useMemo, useRef, useState } from "react";

// Animation / Routing
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router";

// Icons
import {
  X, Mail, Phone, Building2, BadgeCheck, MapPin, UserCircle2, CalendarDays,
  IdCard, Clock, ShieldCheck, ArrowUpRight, Copy, ExternalLink, Power,
  ChevronRight, Briefcase, Monitor, Search, StickyNote, FileText, User, Globe,
  Quote, Building, Calendar, Key, Award, BookOpen, CheckCircle, Palette, Bell,
  Users, Ban, LockKeyhole, Lock, Image, Map, Globe2, Layers, MessageSquare,
  MailCheck,
  PhoneCall,
  DollarSign,
} from "lucide-react";

// Custom hooks & UI


import { useApiCall } from "../../../../../component/custom-hooks/api-call";
import InnerLoader from "../../../../../component/ui/loader/inner-loader";
import StatusChip from "../../../../../component/ui/chip/status";


const tabs = [
  { key: "summary", label: "Summary" }
];
function classNames(...xs) {
  return xs.filter(Boolean).join(" ");
}

// function useOnClickOutside(ref, handler) {
//   useEffect(() => {
//     function listener(event) {
//       if (!ref.current || ref.current.contains(event.target)) return;
//       handler(event);
//     }
//     document.addEventListener("mousedown", listener);
//     document.addEventListener("touchstart", listener);
//     return () => {
//       document.removeEventListener("mousedown", listener);
//       document.removeEventListener("touchstart", listener);
//     };
//   }, [ref, handler]);
// }

function Badge({ children, tone = "default", icon: Icon }) {
  const tones = {
    default: "bg-slate-100 text-slate-700",
    green: "bg-emerald-100 text-emerald-700",
    blue: "bg-blue-100 text-blue-700",
    amber: "bg-amber-100 text-amber-800",
    red: "bg-rose-100 text-rose-700",
    grey: "bg-neutral-100 text-neutral-700",
  };
  return (
    <span className={classNames("inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium", tones[tone])}>
      {Icon ? <Icon size={14} /> : null}
      {children}
    </span>
  );
}

function Stat({ title, value, icon: Icon, hint, tone = "default" }) {
  return (
    <div className="group relative rounded-2xl border border-solid border-slate-200 bg-white p-4 shadow-sm hover:shadow-md">
      <div className="flex items-start gap-3">
        {/* <div className="rounded-xl bg-slate-50 p-2">
          <Icon className="size-5" />
        </div> */}
        <div className="min-w-0">
          <div className="text-xs text-slate-500 capitalize">{title.includes("_") ? title.split("_").map(word => ` ${word}`) : title}</div>
          {/* truncate */}
          <div className="text-lg font-semibold tracking-tight">{value}</div>
          {hint && <div className="mt-1 text-xs text-slate-400">{hint}</div>}
        </div>
      </div>
      {/* {tone !== "default" && (
        <div className="absolute right-3 top-3">
          <Badge tone={tone}>{tone === "green" ? "Good" : tone === "amber" ? "Watch" : "Check"}</Badge>
        </div>
      )} */}
    </div>
  );
}


function Field({ label, value, icon: Icon, copyable, link, fullWidth }) {
  const [copied, setCopied] = useState(false);

  const isUrl = typeof value === "string" && value.startsWith("http") && label == "Case Study";

  return (
    <div
      className={classNames(
        "grid items-center gap-2 rounded-xl border border-solid border-slate-200 bg-white p-3 transition hover:bg-slate-50",
        fullWidth ? "grid-cols-1" : "grid-cols-12"
      )}
    >
      <div
        className={classNames(
          fullWidth
            ? "text-[13px] font-medium text-slate-500 mb-1 flex items-center gap-2"
            : "col-span-4 flex items-center gap-2 text-[13px] font-medium text-slate-500"
        )}
      >
        {Icon ? <Icon className="size-4" /> : null}
        {label}
      </div>
      <div
        className={classNames(
          fullWidth
            ? "text-[13px] text-slate-800"
            : "col-span-8 flex items-center justify-between gap-2 text-[13px] text-slate-800"
        )}
      >
        {isUrl ? (
          <a
            href={value}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 underline hover:text-blue-800"
          >
            Download {label}
          </a>
        ) : link ? (
          <a
            href={link}
            className="truncate underline decoration-slate-300 underline-offset-2 hover:text-blue-600 hover:decoration-blue-400"
          >
            {value}
          </a>
        ) : (
          <span className={fullWidth ? "whitespace-pre-wrap leading-[1.5]" : "truncate"}>
            {value}
          </span>
        )}
      </div>
    </div>
  );
}



export default function RefundQuickView({ open = true, onClose = () => { }, data, contact, refundData, refundPopupHead }) {
  const [activeTab, setActiveTab] = useState("summary");
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState(contact?.contactstatus);
  const containerRef = useRef(null);
  const apiCall = useApiCall();
  const [isLoading, setIsLoading] = useState(false);


  const [caseStudyData, setCaseStudyData] = useState(null);

  useEffect(() => {
    function onKey(e) {
      if (e.key === "Escape") onClose();
      if (e.key.toLowerCase() === "f" && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        const el = document.getElementById("quickview-search");
        el?.focus();
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const summaryItems = () => {
    if (!refundData) return [];

    // If refundData is an array, pick the first item (or map over all if you want multiple)
    const data = Array.isArray(refundData) ? refundData[0] : refundData;

    // Convert each key-value pair into a summary object
    return Object.keys(data).map((key) => ({
      tab: "summary",
      label: key.replace(/_/g, " ").toUpperCase(), // make it nicer for display
      value: data[key],
    }));
  };

  // Usage in your component
  const summary = summaryItems();

  useEffect(() => {
    if (!open) return;

    const fetchCaseStudy = async () => {
      try {
        const res = await apiCall({
          method: "get",
          url: `refund-trackers/get-case-study/${refundPopupHead?.id}`
        });
        if (res?.data?.case_study) {
          setCaseStudyData(res.data?.case_study);
        } else {
          setCaseStudyData(null);
        }
      } catch (err) {
        console.error("Error fetching case study:", err);
        setCaseStudyData(null);
      }
    };

    fetchCaseStudy();
  }, [open, refundPopupHead?.id]);


  const fields = useMemo(() => {
    if (!refundData) return [];

    const data = Array.isArray(refundData) ? refundData[0] : refundData;

    const fullWidthKeys = ["case_study", "qa_analysis"];

    const entries = Object.entries(data);
    const fullWidthEntries = entries.filter(([key]) => fullWidthKeys.includes(key));
    const otherEntries = entries.filter(([key]) => !fullWidthKeys.includes(key));

    const mappedFields = otherEntries.map(([key, value]) => {
      if (key === "email") {
        return {
          tab: "summary",
          label: "Email",
          value,
          copyable: true,
          link: `mailto:${value}`,
        };
      }

      return {
        tab: "summary",
        label: key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
        value,
      };
    });


    fullWidthEntries.forEach(([key, value]) => {
      mappedFields.push({
        tab: "summary",
        label: key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
        value,
        fullWidth: true,
      });
    });


    if (caseStudyData) {
      mappedFields.push({
        tab: "summary",
        label: "Case Study",
        value: caseStudyData.replace(/\\/g, ""),
        fullWidth: true,
      });
    }

    return mappedFields;
  }, [refundData, caseStudyData]);



  // Filter logic remains the same
  const filteredFields = fields.filter(
    (f) =>
      (!query || `${f.label} ${f.value}`.toLowerCase().includes(query.toLowerCase())) &&
      (activeTab === "summary"
        ? ["summary", "work", "personal"].includes(f.tab)
        : f.tab === activeTab)
  );



  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[60] flex items-stretch justify-end bg-slate-900/40 backdrop-blur-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.aside
            ref={containerRef}
            initial={{ x: 40, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: 40, opacity: 0 }}
            transition={{ type: "spring", stiffness: 380, damping: 32 }}
            className="relative mt-auto mr-[2vh] mb-auto ml-0 flex h-[96vh] w-[1020px] max-w-[98vw] flex-col overflow-hidden rounded-3xl border border-solid border-slate-200 bg-gradient-to-b from-white to-slate-50 shadow-xl"
            aria-modal
            role="dialog"
          >
            {

              <>
                {/* Header */}
                <div className="border-b border-solid border-slate-200 bg-white/90 p-4 backdrop-blur supports-[backdrop-filter]:bg-white/70 w-full">
                  <div className="flex flex-wrap items-center justify-between gap-3 w-full">
                    <div className="flex min-w-0 items-center gap-3">
                      {/* <img
                          src={contact?.userimg}
                          alt={contact?.name}
                          className="size-12 rounded-2xl object-cover ring-2 ring-white shadow"
                        /> */}
                      <div className="flex items-center justify-center size-12 bg-slate-200 rounded-full overflow-hidden">
                        <p className='capitalize font-semi-bold text-base text-black'>
                          {refundPopupHead?.client_name?.split(" ")?.map((n, i) => n.charAt(0))}
                        </p>
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <h2 className="truncate text-lg font-semibold tracking-tight text-slate-900">
                            {refundPopupHead?.client_name}
                          </h2>
                          <Badge tone={refundPopupHead?.cb_or_refund == "Refund" ? "blue" : "red"}>{refundPopupHead?.cb_or_refund}</Badge>
                          {/* <Badge tone="blue" icon={Briefcase}>{contact?.role}</Badge> */}
                        </div>
                        <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-slate-500">
                          <span className="inline-flex items-center gap-1"><MailCheck className="size-4" /> {refundPopupHead?.email}</span>

                          {/* <span className="inline-flex items-center gap-1"><DollarSign className="size-4" /> {refundPopupHead?.cb_or_refund}</span> */}
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-2">
                      {/* <Link to={`/contacts/contact/${data?.userid}`}
                        className="inline-flex items-center gap-1.5 rounded-xl border border-solid border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                      // onClick={() => window.alert("Opening full profile… (wire to route)")}
                      >
                        <ExternalLink className="size-4" /> View Full Profile
                      </Link> */}
                      <button
                        className="inline-flex items-center gap-1.5 rounded-xl bg-[#0B54FF] px-3 py-2 text-sm font-medium text-white hover:bg-[#0849e0]"
                        onClick={onClose}
                        aria-label="Close"
                      >
                        <X className="size-4" /> Exit
                      </button>
                    </div>
                  </div>

                  {/* Quick actions */}
                  <div className="mt-4 flex flex-wrap items-center gap-2">
                    <QuickAction icon={Mail} label="Email" href={`mailto:${refundPopupHead?.email}`} />
                    {/* <QuickAction icon={Phone} label="Call" href={`tel:${contact?.phone}`} /> */}
                    {/* <QuickAction icon={MessageSquare} label="Message" onClick={() => window.alert("Open internal chat")} /> */}
                    {/* <QuickAction icon={PencilLine} label="Edit" onClick={() => window.alert("Open edit form")} /> */}
                    {/* <QuickAction
                        icon={Power}
                        label={status === "Active" ? "Deactivate" : "Activate"}
                      // onClick={() => setStatus(s => (s === "Active" ? "Inactive" : "Active"))}
                      /> */}
                    <div className="ml-auto flex items-center gap-2">
                      <kbd className="rounded-md border border-solid bg-white px-1.5 py-1 text-[10px] text-slate-500">⌘/Ctrl</kbd>
                      <span className="text-[10px] text-slate-500">+ F to search</span>
                    </div>
                  </div>
                </div>

                {/* Body */}
                <div className="grid min-h-0 flex-1 grid-cols-1 gap-4 overflow-hidden p-4 w-full">
                  {/* Left: main content */}
                  <div className="col-span-12 flex min-h-0 flex-col gap-4">
                    {/* Top stats */}
                    <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                      {/* 
                          <Stat title="Tenure" value={`${Math.floor(contact?.summary?.tenureMonths / 12)}y ${contact?.summary?.tenureMonths % 12}m`} icon={CalendarDays} tone="green" />
                          <Stat title="KPI Score" value={`${contact?.summary?.kpiScore}%`} icon={Stars} tone={contact?.summary?.kpiScore >= 80 ? "green" : contact?.summary?.kpiScore >= 60 ? "amber" : "red"} />
                          <Stat title="Tasks Overdue" value={contact?.summary?.tasksOverdue} icon={Clock} tone={contact?.summary?.tasksOverdue === 0 ? "green" : "amber"} />
                          <Stat title="Projects this month" value={contact?.summary?.projectsThisMonth} icon={Briefcase} /> 
                        */}

                      {
                        contact?.stats?.map((ele, idx) => {
                          return (
                            <Stat title={ele?.key} value={ele?.value} icon={CalendarDays} />
                          )
                        })
                      }
                    </div>

                    {/* Tabs */}
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex flex-wrap items-center gap-1">
                        {tabs?.map(t => (
                          <button
                            key={t.key}
                            onClick={() => setActiveTab(t.key)}
                            className={classNames(
                              "rounded-md px-[12px] py-[8px] text-sm font-medium border border-solid border-[#E1E4EA]",
                              activeTab === t.key
                                ? "bg-[#F5F5F5] text-[#0E121B]"
                                : "text-slate-600 hover:bg-slate-100"
                            )}
                          >
                            {t.label}
                          </button>
                        ))}
                      </div>
                      <div className="relative">
                        <Search className="pointer-events-none absolute left-3 top-2.5 size-4 text-slate-400" />
                        <input
                          id="quickview-search"
                          placeholder="Search fields…"
                          value={query}
                          onChange={e => setQuery(e.target.value)}
                          className="w-64 rounded-2xl border border-solid border-slate-200 bg-white py-2 pl-9 pr-3 text-sm outline-none placeholder:text-slate-400 focus:ring-2 focus:ring-slate-900/10"
                        />
                      </div>
                    </div>

                    <div className="min-h-0 overflow-auto rounded-2xl py-2">
                      {/* Render fields for current tab (or summary) */}
                      {activeTab === "summary" ? (
                        <div className="space-y-4">

                          {/* ---------- IMPORTANT FIELDS ---------- */}
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {fields
                              .filter((f) => f.tab === "summary")
                              .map((field, idx) => (
                                <div key={idx} className={field.fullWidth ? "col-span-1 md:col-span-2" : ""}>
                                  <Field {...field} />
                                </div>
                              ))}
                          </div>
                        </div>
                      ) : (
                        <div className="grid grid-cols-1 gap-3">
                          {filteredFields?.map((f, idx) => (
                            <Field key={idx} {...f} />
                          ))}

                          {activeTab === "notes" && <Notes />}
                          {activeTab === "assets" && <Assets assets={contact?.assets} />}
                          {activeTab === "activity" && <Activity items={contact?.activity} />}
                          {activeTab === "attendance" && (
                            <div className="space-y-3"> ... </div>
                          )}
                          {activeTab === "transactions" && (
                            <div className="space-y-3">
                              {contact?.transactions?.map(tx => (
                                <Field
                                  key={tx.transactionid}
                                  label={`Transaction (${tx.transactiontype})`}
                                  value={`$${tx.amount} - ${tx.transactionstatus}`}
                                  icon={CheckCircle}
                                />
                              ))}
                            </div>
                          )}
                          {activeTab === "client_deals" && (
                            <div className="space-y-3">
                              {contact?.client_deals?.map(deal => (
                                <Field
                                  key={deal.dealid}
                                  label={`Deal (${deal.deal_package})`}
                                  value={deal.name}
                                  icon={Briefcase}
                                />
                              ))}
                            </div>
                          )}
                          {activeTab === "client_projects" && (
                            <div className="space-y-3">
                              {contact?.client_projects?.map(prj => (
                                <Field
                                  key={prj.projectid}
                                  label={`Project (${prj.invoiceid})`}
                                  value={`${prj.status} - $${prj.projectcost}`}
                                  icon={Layers}
                                />
                              ))}
                            </div>
                          )}
                        </div>
                      )}

                    </div>
                  </div>

                  {/* Right: context column */}
                  {/* <div className="col-span-12 flex min-h-0 flex-col gap-4 lg:col-span-4">
                      <div className="rounded-2xl border border-solid border-slate-200 bg-white p-4">
                        <h3 className="mb-3 flex items-center justify-between text-sm font-semibold">
                          Highlights
                          <Badge tone="blue">Real-time</Badge>
                        </h3>
                        <ul className="space-y-2 text-sm text-slate-700">
                          <li className="flex items-start gap-2"><ArrowUpRight className="mt-0.5 size-4" /> Completed 9/9 hours today</li>
                          <li className="flex items-start gap-2"><ShieldCheck className="mt-0.5 size-4" /> No policy flags in last 90 days</li>
                          <li className="flex items-start gap-2"><Monitor className="mt-0.5 size-4" /> All assets compliant</li>
                        </ul>
                      </div>

                      <div className="rounded-2xl border border-solid border-slate-200 bg-white p-4">
                        <h3 className="mb-3 text-sm font-semibold">Tags</h3>
                        <div className="flex flex-wrap gap-2">
                          {contact?.tags?.map(t => (
                            <span key={t} className="rounded-full bg-slate-100 px-3 py-1 text-xs text-slate-700">{t}</span>
                          ))}
                          <button className="rounded-full border border-dashed border-slate-300 px-3 py-1 text-xs text-slate-500 hover:bg-slate-50">+ Add</button>
                        </div>
                      </div>

                      <div className="min-h-0 flex-1 overflow-auto rounded-2xl border border-solid border-slate-200 bg-white p-4">
                        <h3 className="mb-3 text-sm font-semibold">Recent Activity</h3>
                        <Activity items={contact?.activity} compact />
                      </div>
                    </div> */}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between gap-2 border-t border-solid border-slate-200 bg-white p-3 w-full">
                  <div className="flex items-center gap-2 text-xs text-slate-500">
                    {/* <IdCard className="size-4" /> ID {refundData?.id}
                    <span>•</span>
                    <CalendarDays className="size-4" /> Requests Date {refundData?.request_date} */}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm hover:bg-slate-50"
                      onClick={() => {
                        setActiveTab('notes');
                        setTimeout(() => {
                          const el = document.getElementById("addNotesField");
                          el?.focus();
                        }, 250);
                      }}>
                      <StickyNote className="mr-1 inline size-4" />
                      Add Note
                    </button>
                    {/* <button className="rounded-xl bg-slate-900 px-3 py-2 text-sm text-white hover:bg-slate-800">Save Changes</button> */}
                  </div>
                </div>
              </>

            }

          </motion.aside>
        </motion.div>
      )}
    </AnimatePresence>
  );
}


// console.log(refunddata)
function QuickAction({ icon: Icon, label, href, onClick }) {
  const className = "inline-flex items-center gap-1.5 rounded-xl border border-solid border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50";
  if (href) return (
    <a className={className} href={href}>
      <Icon className="size-4" /> {label}
    </a>
  );
  return (
    <button className={className} onClick={onClick}>
      <Icon className="size-4" /> {label}
    </button>
  );
}

function Activity({ items, compact = false }) {
  return (
    <ul className="space-y-3">
      {items?.map((a, i) => (
        <li key={i} className="flex items-start gap-3">
          <div className="mt-1 h-5 w-5 rounded-full bg-slate-100 ring-1 ring-slate-200" />
          <div className="min-w-0 flex-1">
            <div className="text-[13px] text-slate-800 pb-[5px]">{a.text}</div>
            <div className="text-[11px] text-slate-400">{formatTs(a.ts)}</div>
          </div>
        </li>
      ))}
      {!compact && (
        <button className="mt-1 inline-flex items-center gap-1 text-xs text-slate-600 hover:underline">
          View full activity <ChevronRight className="size-4" />
        </button>
      )}
    </ul>
  );
}

function Assets({ assets }) {
  return (
    <div className="space-y-3">
      {assets?.map((a, i) => (
        <div key={i} className="flex items-center justify-between rounded-xl border border-solid border-slate-200 bg-white p-3">
          <div className="flex min-w-0 items-center gap-3">
            <Monitor className="size-5" />
            <div className="min-w-0">
              <div className="truncate text-sm font-medium">{a.name}</div>
              <div className="truncate text-xs text-slate-500">{a.id}</div>
            </div>
          </div>
          <Badge tone={a.status === "Assigned" ? "blue" : "grey"}>{a.status}</Badge>
        </div>
      ))}
      <button className="inline-flex items-center gap-1 text-xs text-slate-600 hover:underline">
        Manage assets <ChevronRight className="size-4" />
      </button>
    </div>
  );
}

function Notes() {
  const [notes, setNotes] = useState([
    {
      id: 1,
      text: "Great initiative on latest branding sprint. Keep momentum for Q4.",
      ts: Date.now() - 86400000,
    },
  ]);
  const [text, setText] = useState("");
  return (
    <div className="space-y-3">
      <div className="rounded-2xl border border-solid border-slate-200 bg-white p-3">
        <textarea
          id="addNotesField"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Add a manager note (visible to HR/Managers)…"
          className="min-h-[90px] w-full resize-y rounded-xl border border-solid border-slate-200 bg-slate-50 p-3 text-sm outline-none focus:ring-2 focus:ring-slate-900/10"
        />
        <div className="mt-2 flex justify-end">
          <button
            onClick={() => {
              if (!text.trim()) return;
              setNotes((n) => [{ id: Date.now(), text, ts: Date.now() }, ...n]);
              setText("");
            }}
            className="rounded-xl bg-slate-900 px-3 py-1.5 text-xs text-white hover:bg-slate-800"
          >
            Save Note
          </button>
        </div>
      </div>
      <ul className="space-y-2">
        {notes?.map((n) => (
          <li key={n.id} className="rounded-xl border border-solid border-slate-200 bg-white p-3">
            <div className="text-sm text-slate-800">{n.text}</div>
            <div className="text-[11px] text-slate-400">{new Date(n.ts).toLocaleString()}</div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function MiniStat({ label, value }) {
  return (
    <div className="rounded-xl border border-solid border-slate-200 bg-slate-50 p-3 text-center">
      <div className="text-[11px] text-slate-500">{label}</div>
      <div className="text-sm font-semibold text-slate-800">{value}</div>
    </div>
  );
}

function formatDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return iso;
  }
}

function formatTs(ts) {
  try {
    const d = typeof ts === "string" ? new Date(ts.replace(" ", "T")) : new Date(ts);
    return d.toLocaleString();
  } catch {
    return String(ts);
  }
}

function capitalize(s) {
  return s.charAt(0).toUpperCase() + s.slice(1);
}
